/**
 * The strip that can contradict the happy path (TASKS §C14.4).
 *
 * For eight days this panel showed two campaigns as `sending` while nothing was
 * sending, because no screen had any way to say otherwise. That is C12, and the
 * lesson was not that a health check was missing — it was that **every surface
 * agreed with the optimistic reading**, so the only way to find out was to go
 * and look at `cron.job`.
 *
 * So this sits under the masthead on every signed-in screen, and it is loud
 * exactly twice: when the clock is stopped, and when the money is nearly gone.
 * The rest of the time it is one quiet line, because a banner that shouts on a
 * good day is a banner nobody reads on a bad one.
 *
 * **It is fetched once and cached.** `renderShell` runs on every navigation,
 * and a request per screen would put a health check in front of every click for
 * information that changes on the scale of minutes.
 */

import { health, type Health } from '../data.ts';
import { el, number, type Child } from '../dom.ts';
import { translate as t } from '../i18n.ts';
import { healthView, type HealthView } from '../../health-view.ts';

/**
 * A minute.
 *
 * Long enough that clicking around the panel is one request rather than a
 * dozen, short enough that somebody who has just been told the clock is stopped
 * sees it start again without reloading.
 */
const FRESH_FOR_MS = 60_000;

let cached: { readonly at: number; readonly answer: Health | undefined } | undefined;
let inFlight: Promise<void> | undefined;

/**
 * The strip, and the fetch behind it.
 *
 * Returns a node immediately and fills it in when the answer arrives, because
 * the alternative is every screen in the panel waiting on a status check before
 * it draws anything.
 */
export function healthStrip(): Child {
  const strip = el('div', { class: 'health', role: 'status' });

  const draw = () => replaceStrip(strip, healthView(cached?.answer));

  if (cached !== undefined && Date.now() - cached.at < FRESH_FOR_MS) {
    draw();
    return strip;
  }

  // Nothing is drawn until there is something to say. An empty strip that
  // becomes a warning is less alarming than a "checking…" that becomes one.
  inFlight ??= health()
    .then((answer) => {
      cached = { at: Date.now(), answer };
    })
    .catch(() => {
      // A failed check is `unknown`, which the view renders as "could not
      // read" — never as healthy.
      cached = { at: Date.now(), answer: undefined };
    })
    .finally(() => {
      inFlight = undefined;
    });

  void inFlight.then(draw);
  return strip;
}

/** Forces the next render to ask again — used after something that changes it. */
export function forgetHealth(): void {
  cached = undefined;
}

function replaceStrip(strip: Element, view: HealthView): void {
  strip.replaceChildren();
  strip.className = `health ${tone(view)}`;

  // The stopped clock, first and in words. It is the one state where the panel
  // is actively misleading without this line.
  if (view.pipeline === 'stopped') {
    strip.appendChild(el('strong', {}, t('health.stopped')));
    strip.appendChild(el('span', {}, t('health.stoppedHelp')));
    if (view.queued > 0) {
      strip.appendChild(el('span', { class: 'health-item' }, t('health.queued', { count: number(view.queued) })));
    }
  } else if (view.pipeline === 'unknown' && view.unavailable) {
    strip.appendChild(el('span', {}, t('health.unavailable')));
  } else if (view.pipeline === 'running') {
    strip.appendChild(el('span', { class: 'health-item ok' }, `${t('health.pipeline')}: ${t('health.running')}`));
  }

  if (view.balance !== undefined) {
    strip.appendChild(
      el(
        'span',
        { class: view.balanceLow ? 'health-item is-low' : 'health-item' },
        `${t('health.balance')}: ${view.balance.toLocaleString('ru-RU')}`,
        view.parts === undefined ? false : el('small', {}, ` ${t('health.balanceParts', { parts: number(view.parts) })}`),
      ),
    );
  }

  if (view.balanceLow) strip.appendChild(el('strong', {}, t('health.balanceLow')));
}

/**
 * A stopped clock outranks everything: it is the state where the rest of the
 * panel is telling you something untrue.
 */
function tone(view: HealthView): string {
  if (view.pipeline === 'stopped') return 'is-bad';
  if (view.balanceLow) return 'is-warn';
  if (view.pipeline === 'unknown') return 'is-muted';
  return 'is-ok';
}
